import { Separator } from '@/components/ui/separator'
import { createFileRoute, redirect, useRouteContext } from '@tanstack/react-router'
import type { SortingState } from '@tanstack/react-table'
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { getIdTokenUsingQueryClient } from '@/data/queries'
import { Button } from '@/components/ui/button'
import {
  useTrashColumns,
  TrashTable,
} from '@/components/dashboard/trash-page/TrashTable'
import DocumentsSidebar from '@/components/dashboard/documents-page/DocumentsSidebar'
import ErrorDocumentPage from '@/components/dashboard/documents-page/ErrorDocumentPage'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import {
  orgPermissionsCurrentUserOptions,
  trashDocumentsOptions,
  tenantProjectsOptions,
} from '@/data/queryOptions'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { DashboardLayout } from '@/routes/_authenticated/_dashboard'
import {
  filterDocumentsByAccess,
  getVisibleProjects,
  getWorkspaceAccess,
} from '@/data/entities'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/documents/trash/',
)({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: 'Trash - LineWise',
      },
    ],
  }),
  loader: async ({ context, params }) => {
    const { queryClient, authState } = context
    const TENANT_ID = params.orgId
    // Sync store with URL param
    const store = useSelectedTenantStore.getState()
    if (TENANT_ID && store.selectedTenantId !== TENANT_ID) {
      store.setSelectedTenantId(TENANT_ID)
    }
    if (!TENANT_ID) {
      throw redirect({ to: '/select-tenant' })
    }
    const idToken = await getIdTokenUsingQueryClient({
      queryClient,
      authState: authState!,
    })
    return Promise.all([
      queryClient.ensureQueryData(trashDocumentsOptions(idToken, TENANT_ID)),
      queryClient.ensureQueryData(tenantProjectsOptions(TENANT_ID!, idToken!)),
    ])
  },
  errorComponent: ({ error }) => <ErrorDocumentPage error={error} />,
})

function RouteComponent() {
  const { isOrgAdmin, isOrgEditor } = useRouteContext({
    from: '/_authenticated',
  })
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const isMobile = useIsMobile()

  const { data: documents } = useSuspenseQuery(
    trashDocumentsOptions(idToken, TENANT_ID!),
  )

  const { data: projects } = useSuspenseQuery(
    tenantProjectsOptions(TENANT_ID!, idToken!),
  )
  const { data: orgPermissions } = useSuspenseQuery(
    orgPermissionsCurrentUserOptions(TENANT_ID!, idToken!),
  )
  const workspaceAccess = useMemo(
    () => getWorkspaceAccess(orgPermissions, TENANT_ID!),
    [orgPermissions, TENANT_ID],
  )
  const visibleProjects = useMemo(
    () =>
      getVisibleProjects(projects ?? [], {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
        workspaceAccess,
      }),
    [projects, workspaceAccess, isOrgAdmin, isOrgEditor],
  )
  const visibleDocuments = useMemo(
    () =>
      filterDocumentsByAccess(documents ?? [], workspaceAccess, {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
      }),
    [documents, workspaceAccess, isOrgAdmin, isOrgEditor],
  )

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'createdAt',
      desc: true,
    },
  ])
  const columns = useTrashColumns()
  const table = useReactTable({
    data: visibleDocuments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: 30, // TODO: set up pagination from server
      },
    },
    columnResizeMode: 'onChange',
    defaultColumn: {
      size: 80,
      minSize: 80,
    },
  })

  return (
    <DashboardLayout>
      <main
        className={cn('main-container', isMobile && 'mt-15 mb-20 flex-col')}
      >
        <DocumentsSidebar projects={visibleProjects} />
        <div className="dashboard-page-container">
          <div className="flex items-center justify-between">
            <h1 className="text-4xl font-bold">Trash</h1>
            <Button variant="outline">Empty Trash</Button>
          </div>
          <Separator />
          <TrashTable table={table} />
        </div>
      </main>
    </DashboardLayout>
  )
}
